import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import React from "react";
import { useSparePartsSubpage } from "../sitemap/use-plp-subpage";
import { plpSubpageTags } from "../sitemap/plp-subpage";
import { css } from "@emotion/react";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";
import { MQ_DISPLAY } from "../../../styles/breakpoints";
import { LinkableContent } from "./content";
import { getStepProps, Step } from "./step";
import { SparePartsMobileNavigation } from "./spare-parts-mobile-navigation";
import { SparePartsProductListingPageTranslation } from "@swo/storefront";
import { LocalizedNodeRef } from "../../../components/_shared-components/generic-listing-page/types";

export const SparePartsNavigation: ReactTFC<{
  BackNavigation: React.ReactElement | undefined;
  category?: LocalizedNodeRef;
  subfamily?: LocalizedNodeRef;
  product?: LocalizedNodeRef;
  haveAnySpareParts?: boolean;
  translations: SparePartsProductListingPageTranslation;
}> = props => {
  const { category, subfamily, product, haveAnySpareParts, translations } = props;
  const { subpage, pathname, sitemapLinker } = useSparePartsSubpage();

  function getStepsList(): Array<Step> {
    return [
      category
        ? {
            key: "category",
            label: category.name,
            link: sitemapLinker("category", { categoryKey: category.key }),
          }
        : {
            key: "category",
            label: translations.category,
            link: undefined,
          },
      category && subfamily
        ? {
            key: "subfamily",
            label: subfamily.name,
            link: sitemapLinker("category", {
              categoryKey: category.key,
            }),
          }
        : {
            key: "subfamily",
            label: translations.productFamily,
            link: undefined,
          },
      category && subfamily && product
        ? {
            key: "product",
            label: product.name,
            link: sitemapLinker("subfamily", {
              categoryKey: category.key,
              subfamilyKey: subfamily.key,
            }),
          }
        : {
            key: "product",
            label: translations.model,
            link: undefined,
          },
      {
        key: "spare-parts",
        label: translations.yourSpareParts,
        link: undefined,
        disabled: category && subfamily && product ? false : undefined,
      },
    ];
  }

  const steps = getStepsList();

  function getStepsNavigation() {
    const previousStep = getStepProps(steps, subpage.tag, -1);
    const currentStep = getStepProps(steps, subpage.tag, 0);
    const nextStep = getStepProps(steps, subpage.tag, 1);

    return { previousStep, currentStep, nextStep };
  }

  const { previousStep, currentStep, nextStep } = getStepsNavigation();

  return (
    <>
      <SparePartsMobileNavigation previousStep={previousStep} currentStep={currentStep} nextStep={nextStep} />

      <div
        css={[
          MQ_DISPLAY.onlyAtLeastMd,
          css`
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            margin-bottom: calc(30px + ${CSS_SPACER_SIZES.lg});

            --size: 50px;
            --font-size: 30px;
          `,
        ]}
      >
        <div />

        <ul
          css={css`
            display: flex;
            justify-content: center;

            --gap: 160px;
            gap: var(--gap);
          `}
        >
          {steps.map((step, idx) => {
            const { className, isCurrentlySelected, text } = getStepProps(
              steps,
              subpage.tag,
              -currentStep.currentIdx + idx,
            );

            return (
              <li
                key={idx}
                className={className}
                css={css`
                  position: relative;
                  &:not(:last-of-type) {
                    &:after {
                      position: absolute;
                      content: "";

                      --g: calc(var(--gap) / 4);
                      height: 1px;
                      top: 50%;

                      width: calc(var(--gap) - 2 * var(--g));
                      left: calc(100% + var(--g));

                      background-color: ${CSS_COLORS.Grey2};
                    }
                  }
                `}
              >
                <LinkableContent
                  link={step.link}
                  isCurrentlySelected={isCurrentlySelected}
                  label={step.label}
                  text={text}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

export const wrapperStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;

  text-decoration: none;

  position: relative;
`;

export const xxxStyles = css`
  .checked & {
    color: ${CSS_COLORS.Black};
  }

  .active & {
    color: ${CSS_COLORS.Green};
  }

  .disabled & {
    color: ${CSS_COLORS.Grey2};
  }

  color: ${CSS_COLORS.Grey2};
`;

export const subpageTagOrder = [
  plpSubpageTags.root,
  plpSubpageTags.facetedSearch,
  plpSubpageTags.category,
  plpSubpageTags.subfamily,
  plpSubpageTags.product,
  plpSubpageTags.productSpareParts,
];
